import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'bootstrap/hooks';
import { goToNextQuestion } from 'bootstrap/onboarding/actions';
import { easeInOutQuad } from 'hooks/useProgress';
import { useTranslation } from 'react-i18next';
import { useFeatures } from 'useFeatures';
import { ProgressBar } from './progressbar/ProgressBar';
import { ReviewCarousel } from './reviewcarousel/ReviewCarousel';
import { QuestionAlert } from './questionalert/QuestionAlert';
import styles from './styles.module.scss';

const ALERT_PROGRESS = [0.75, 0.9, 0.55, 0.85];

const STEP_DURATION = 5000;

export const LivenAnalysisLoader = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const features = useFeatures();

  const { isPersonalizationLoaderEstimateFlow } = features;

  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(0);
  const [alertShown, setAlertShown] = useState(false);
  const [progressPaused, setProgressPaused] = useState(false);

  const ALERTS: { title: string; id: number }[] = [
    'Are you inclined to finish what you start?',
    'Are you familiar with journaling for self-reflection?',
    'Do you want to learn how to build strong habits with ADHD?',
  ].map((it, index) => {
    return {
      id: index,
      title: it,
    };
  });

  useEffect(() => {
    if (!progressPaused) {
      const startTime = Date.now();
      const pausePercent = ALERT_PROGRESS[step];
      const currentLineInPercent =
        progress < pausePercent ? pausePercent : 1 - pausePercent;
      const stepDuration = STEP_DURATION * currentLineInPercent;

      const timer = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const nextProgress = easeInOutQuad(
          Math.min(1, elapsedTime / stepDuration),
        );
        if (progress < pausePercent && nextProgress >= 1) {
          setProgress(nextProgress * currentLineInPercent);
          setAlertShown(true);
          setProgressPaused(true);
          clearInterval(timer);
        } else {
          const prefix = progress >= pausePercent ? pausePercent : 0;
          const nextValue = prefix + nextProgress * currentLineInPercent;
          if (nextValue >= 1) {
            if (step < ALERTS.length - 1) {
              setProgress(0);
              setStep(step + 1);
              clearInterval(timer);
            } else {
              setProgress(1);
              setTimeout(() => {
                dispatch(goToNextQuestion(features));
              }, 500);
              clearInterval(timer);
            }
          } else {
            setProgress(nextValue);
          }
        }
      }, 100);
      return () => {
        clearInterval(timer);
      };
    }
  }, [progressPaused, step]);

  const getProgressValue = (p: number) => {
    if (step < p) {
      return 0;
    } else if (step === p) {
      return progress;
    } else {
      return 1;
    }
  };

  const handleAnswer = () => {
    if (step < ALERTS.length) {
      setAlertShown(false);
      setTimeout(() => {
        setProgressPaused(false);
      }, 250);
    } else {
      setAlertShown(false);
    }
  };

  return (
    <div className={`${styles.container} maxWidth`}>
      <QuestionAlert
        visible={alertShown}
        // @ts-ignore
        question={ALERTS[step]}
        onAnswer={handleAnswer}
      />
      <h2 className={styles.title}>
        Creating your <br /> <span>personalized ADHD management plan</span>
      </h2>
      {isPersonalizationLoaderEstimateFlow && (
        <h4 className={styles.subtitle}>
          {t('PersonalizationLoaderSubtitle')}
        </h4>
      )}
      <ProgressBar
        text="Goals"
        color="#429e71"
        progress={getProgressValue(0)}
      />
      <ProgressBar
        text="Adapting growth areas"
        color="#429e71"
        progress={getProgressValue(1)}
      />
      <ProgressBar
        text="Picking content"
        color="#429e71"
        progress={getProgressValue(2)}
      />
      <ReviewCarousel />
    </div>
  );
};
